/**
 * React-query hooks for management-fee invoices (owner billing).
 *
 * Source of truth: public.management_invoices. Paid rows sum to the dashboard
 * "Management revenue" card, so every mutation invalidates both this list and
 * the analytics management key.
 */
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import type { ManagementInvoice, InvoiceStatus } from "@/types/billing";
import {
  canMarkInvoicePaid,
  isOffsetInvoice,
  OFFSET_NET_PAYOUT_METADATA_KEY,
} from "@/types/billing";
import type { InvoiceSettlement } from "@/types/billing";
import type { Json } from "@/lib/supabase-types";
import { invalidateOwnerCaches } from "@/hooks/v2/owner-detail-keys";
import type { NonBillableReasonCode } from "@/lib/owner-metrics";

const PROOF_BUCKET = "contracts";

export const MANAGEMENT_INVOICE_KEYS = {
  list: () => ["v2", "management-invoices", "list"] as const,
  analytics: () => ["admin-analytics-management-invoices"] as const,
};

/** A management invoice joined with owner (lessor) + property for display. */
export type ManagementInvoiceRow = ManagementInvoice & {
  lessors?: {
    id: string;
    name: string | null;
    email: string | null;
    address: string | null;
    city_postal: string | null;
  } | null;
  properties?: {
    id: number;
    title: string | null;
    city: string | null;
    /** CoLiviq-held account for this property — resolves the invoice's "pay to" bank. */
    platform_bank_account_id: number | null;
  } | null;
  /** Status for display: a still-pending invoice past its due date reads as overdue. */
  displayStatus?: InvoiceStatus;
  /**
   * Client-only placeholder for a month with no real row yet (mirrors the
   * tenant rent tab's projected rows) — never persisted. Its row actions
   * materialise a real invoice on demand instead of acting on `id`.
   */
  __projected?: boolean;
  /**
   * Client-only: which UNIT a projected row forecasts, and whether the
   * generator would bill it. A projection is now one apartment (or one
   * property's unassigned rooms) rather than a whole month, so the row has to
   * carry the unit's identity — there is no invoice yet to read it off.
   * Never persisted. See projectOwnerUnitInvoices.
   */
  __unitTitle?: string;
  __unitName?: string;
  __unitPropertyTitle?: string | null;
  __unitApartmentId?: number | null;
  __unitPropertyId?: number;
  /** False for a unit that earns no fee — the row shows the reason, not actions. */
  __unitBillable?: boolean;
  __unitReason?: NonBillableReasonCode | null;
  /**
   * How the invoice this row forecasts will settle — read off the unit's
   * collection mode, so materialising the row writes the same `settlement` the
   * monthly generator would have.
   */
  __unitSettlement?: InvoiceSettlement;
};

/**
 * fee_basis for an invoice an admin created by hand, rather than one
 * generate_management_invoices computed from occupancy.
 *
 * Deliberately carries NO per-property breakdown and no occupancy figures. The
 * generator's fee_basis describes ONE month: `properties[]` holds that month's
 * per-property `fee_eur`, and managementInvoiceLines() itemises off it while
 * reconciling NETTO against `amount_cents` (see src/lib/management-invoice-lines.ts).
 * Copying it onto another month would bill August's line items against
 * October's total — the PDF would name the wrong properties AND its NETTO
 * column would not foot. With no `properties`, the document renders a single
 * line reconciled off the confirmed gross, and managementBasisText() falls
 * back to the generic "Co-Living-Management" basis rather than asserting an
 * occupancy that was never computed for this month.
 */
export const MANUAL_FEE_BASIS: { [key: string]: Json } = { level: "manual" };

/**
 * fee_basis for a hand-created invoice, naming the UNIT it bills.
 *
 * Still `level: "manual"` — no occupancy, no computed fee, nothing copied from
 * another month (see MANUAL_FEE_BASIS). The one addition is the unit's identity
 * in a single-entry `properties[]`, because that array is where
 * managementInvoiceLines reads a document's line title from: without it, a
 * hand-created invoice for one apartment prints the building's name, the same
 * line every other flat in it would print.
 *
 * `fee_eur` is deliberately absent. It is the field that describes a computed
 * month, and the line's NET is split from the invoice's own authoritative
 * amount_cents anyway.
 */
export function manualUnitFeeBasis(unit: {
  apartment_id?: number | null;
  property_id?: number | null;
  unit_title?: string | null;
  unit_name?: string | null;
  property_title?: string | null;
}): { [key: string]: Json } {
  if (!unit.unit_title) return MANUAL_FEE_BASIS;
  return {
    ...MANUAL_FEE_BASIS,
    properties: [
      {
        property_id: unit.property_id ?? null,
        apartment_id: unit.apartment_id ?? null,
        // Both names, exactly as generate_management_invoices writes them:
        // apartment_label is the qualified line title for the PDF, unit_name
        // and property_title are the two halves the Invoices tab heads a card
        // with. Writing only the label made a hand-created invoice head its
        // card differently from the cron's invoice for the same apartment.
        apartment_label: unit.unit_title,
        unit_name: unit.unit_name ?? unit.unit_title,
        property_title: unit.property_title ?? null,
      },
    ],
  };
}

/**
 * Derive the status to show. No job transitions invoices to "overdue", so a
 * pending invoice past its due_date is surfaced as overdue at read time — this
 * is what drives the overdue badge/count.
 *
 * An OFFSET invoice is never overdue, whatever its dates say. The generator
 * writes it with due_date NULL and the immutability trigger freezes both fields
 * after the send, so this guard is belt-and-braces — but it is the one place
 * the whole product decides who gets dunned, and a stray due date on a fee
 * already deducted from an owner's rent must not turn into a red badge and a
 * chase. Defence here, not in each of the four surfaces that read it.
 */
export function managementDisplayStatus(
  row: Pick<ManagementInvoice, "status" | "due_date"> & {
    settlement?: InvoiceSettlement | null;
  }
): InvoiceStatus {
  if (row.status === "pending" && row.due_date && !isOffsetInvoice(row)) {
    const due = new Date(`${String(row.due_date).slice(0, 10)}T23:59:59`);
    if (!Number.isNaN(due.getTime()) && due < new Date()) return "overdue";
  }
  return row.status;
}

/**
 * Every management invoice (with owner + property joins) — the shared source
 * for the Payments hub and the Owners → Invoices tab. `enabled: false` lets a
 * surface that only needs the list sometimes (e.g. the owner modal, which is
 * mounted closed) skip the fetch until it opens.
 */
export function useManagementInvoices(options?: { enabled?: boolean }) {
  return useQuery({
    enabled: options?.enabled ?? true,
    queryKey: MANAGEMENT_INVOICE_KEYS.list(),
    queryFn: async (): Promise<ManagementInvoiceRow[]> => {
      const { data, error } = await supabase
        .from("management_invoices")
        .select(
          `*,
           lessors:lessor_id ( id, name, email, address, city_postal ),
           properties:property_id ( id, title, city, platform_bank_account_id )`
        )
        // Canceled invoices are voided — exclude them from the operational list
        // so they never pollute owner group totals/counts or offer a stray
        // "Mark paid" action. (PropertiesList already ignores them; analytics
        // uses its own query.) Consolidating per-apartment rows into a single
        // per-lessor invoice leaves the old rows canceled — this keeps them out.
        .neq("status", "canceled")
        .order("period_month", { ascending: false })
        .order("invoice_no", { ascending: false });
      if (error) throw error;
      return ((data ?? []) as ManagementInvoiceRow[]).map((row) => ({
        ...row,
        displayStatus: managementDisplayStatus(row),
      }));
    },
  });
}

/**
 * The `code` on markPaid's refusal to mark a canceled invoice paid (COL-483),
 * so the caller can show it as translated copy.
 */
export const MARK_PAID_CANCELED = "mark_paid_canceled";

/** The `code` on unmarkPaid's refusal when the invoice is no longer paid
 *  (COL-483): canceled, or unmarked elsewhere, since the page loaded. */
export const UNMARK_PAID_NOT_PAID = "unmark_paid_not_paid";

/**
 * Merge a patch into one invoice's metadata, server-side (issue #1717).
 *
 * The callers below each own a couple of keys and have no opinion about the
 * rest, so they send only those. `merge_management_invoice_metadata` applies
 * `metadata || patch` in a single statement, which is what makes a key written
 * by someone else between render and save survive. SECURITY INVOKER, so the
 * admin RLS policy is still the gate.
 *
 * A JSON null in the patch is a VALUE, not a deletion: `proof_note: null` is
 * how a note is cleared while the key stays.
 */
async function mergeInvoiceMetadata(
  id: string,
  patch: Record<string, unknown>
): Promise<{ error: unknown }> {
  const { error } = await (
    supabase as unknown as {
      rpc: (
        fn: string,
        args: Record<string, unknown>
      ) => Promise<{
        error: unknown;
      }>;
    }
  ).rpc("merge_management_invoice_metadata", { _id: id, _patch: patch });
  return { error };
}

export function useManagementInvoiceMutations() {
  const qc = useQueryClient();
  const invalidate = () => {
    qc.invalidateQueries({ queryKey: MANAGEMENT_INVOICE_KEYS.list() });
    qc.invalidateQueries({ queryKey: MANAGEMENT_INVOICE_KEYS.analytics() });
    // The owner detail aggregates these invoices (Overview stats + the
    // Invoices tab), so a paid/proof change there must refresh with the row.
    // Via invalidateOwnerCaches rather than OWNER_DETAIL_PREFIX alone,
    // because the owners LIST derives from these invoices too — its
    // "[Month] payouts ready" tile (COL-461) counts groups whose offset
    // invoice is not yet paid, and confirming that offset is normally done
    // from the owner modal opened on top of that very list, which stays
    // mounted and cached. Naming only owner-detail left the tile showing the
    // pre-settlement count; this is the "hand-listing keys" trap that helper
    // exists to prevent.
    invalidateOwnerCaches(qc);
  };

  const uploadProof = useMutation({
    mutationFn: async ({
      id,
      file,
      metadata,
      note,
    }: {
      id: string;
      file: File;
      /**
       * Existing invoice metadata to merge into. Callers that already hold the
       * row (PaymentsHub) pass it so a metadata write can't clobber other keys.
       */
      metadata?: Record<string, unknown> | null;
      /**
       * Optional note captured alongside the proof. Provided → recorded on the
       * invoice; omitted → the previous proof's note is cleared (see below).
       */
      note?: string;
    }) => {
      const ext = file.name.split(".").pop() || "pdf";
      const safe = file.name.replace(/[^a-zA-Z0-9._-]/g, "_");
      const path = `management-proofs/${id}/${Date.now()}-${safe}.${ext}`;
      const up = await supabase.storage
        .from(PROOF_BUCKET)
        .upload(path, file, { upsert: false });
      if (up.error) throw up.error;

      // A new proof supersedes the previous proof's note. If a fresh note was
      // captured at upload time, record it (with its timestamp); otherwise clear
      // the stale one — a note describing the replaced file must never keep
      // "Mark paid" enabled. The note gate then demands a new note (via
      // addProofNote), mirroring deleteProof.
      //
      // `metadata` is no longer read (issue #1717). It used to be spread into
      // the write so other keys survived, which is exactly how a payout's
      // recorded net got dropped: the snapshot predated the confirmation that
      // wrote it. Only the two keys this mutation owns are sent now, and the
      // server merges. The parameter is kept so callers need not change, and
      // because dropping it would silently turn every caller's "merge into
      // this" into something else.
      void metadata;
      const trimmedNote = note?.trim();
      const nextMetadata = trimmedNote
        ? {
            proof_note: trimmedNote,
            proof_note_at: new Date().toISOString(),
          }
        : { proof_note: null, proof_note_at: null };

      // ORDER MATTERS. These were one UPDATE before #1717 split the metadata
      // out, and splitting them means one can land without the other. The note
      // is cleared FIRST so the failure mode is safe: a merge that succeeds and
      // a column update that fails leaves the OLD proof with no note, and
      // canMarkInvoicePaid needs a non-empty note — the gate closes. The other
      // order leaves the NEW proof wearing the REPLACED file's note, which
      // satisfies that gate while describing a different document.
      const { error: mergeError } = await mergeInvoiceMetadata(
        id,
        nextMetadata
      );
      if (mergeError) {
        await supabase.storage.from(PROOF_BUCKET).remove([path]);
        throw mergeError;
      }

      const { error } = await supabase
        .from("management_invoices")
        .update({
          proof_url: path,
          proof_uploaded_at: new Date().toISOString(),
        })
        .eq("id", id);
      if (error) {
        await supabase.storage.from(PROOF_BUCKET).remove([path]);
        throw error;
      }
    },
    onSuccess: invalidate,
  });

  const markPaid = useMutation({
    mutationFn: async (row: ManagementInvoice) => {
      // Enforce the proof + note rule on the client too (RLS/DB stays open for admin backfills).
      if (!canMarkInvoicePaid(row)) {
        throw new Error(
          isOffsetInvoice(row)
            ? "Add a note recording the deduction before settling this invoice."
            : "Upload a payment proof and add a note before marking this invoice paid."
        );
      }
      const { data, error } = await supabase
        .from("management_invoices")
        .update({ status: "paid", paid_at: new Date().toISOString() })
        .eq("id", row.id)
        // COL-483: never a canceled row. Undoing a payout cancels the
        // remainder it opened on its own (absorbed), and marking that paid
        // from a window opened before would bill its rent a second time.
        .neq("status", "canceled")
        .select("id");
      if (error) throw error;
      if (!data?.length) {
        throw Object.assign(
          new Error(
            "This invoice has been canceled, so it can't be marked paid."
          ),
          { code: MARK_PAID_CANCELED }
        );
      }
    },
    onSuccess: invalidate,
  });

  const unmarkPaid = useMutation({
    mutationFn: async (id: string) => {
      const { data, error } = await supabase
        .from("management_invoices")
        .update({ status: "pending", paid_at: null })
        .eq("id", id)
        // COL-483: only a row that is still paid. A stale window must not
        // bring back a remainder canceled since it loaded (absorbed or
        // reissued): that would bill its rent a second time.
        .eq("status", "paid")
        .select("id");
      if (error) throw error;
      if (!data?.length) {
        throw Object.assign(
          new Error("This invoice is no longer marked paid."),
          { code: UNMARK_PAID_NOT_PAID }
        );
      }
    },
    onSuccess: invalidate,
  });

  // The mandatory note captured after a proof is uploaded. Stored in the
  // invoice's metadata (no dedicated column); required before "Mark paid".
  //
  // NOTE (concurrency): addProofNote/deleteProof/uploadProof each write a
  // metadata snapshot captured at render time, so two admins on the same invoice
  // are last-write-wins and a racing write could drop an entry from the
  // proof_deletions audit array. Acceptable on this admin-only screen; if that
  // ever matters, move to a server-side merge RPC (metadata = metadata || $1).
  const addProofNote = useMutation({
    mutationFn: async ({
      id,
      note,
      metadata,
    }: {
      id: string;
      note: string;
      metadata: Record<string, unknown>;
    }) => {
      // Only the two keys this mutation owns. Spreading `metadata` here is
      // what issue #1717 is: a note saved after a payout was confirmed
      // replaced the whole object with one captured before it, dropping
      // metadata.net_payout_cents and with it the owner's statement.
      void metadata;
      const { error } = await mergeInvoiceMetadata(id, {
        proof_note: note,
        proof_note_at: new Date().toISOString(),
      });
      if (error) throw error;
    },
    onSuccess: invalidate,
  });

  // Materialise a projected (not-yet-issued) unit-month into a real invoice —
  // the same shape generate_management_invoices writes since 20260904120000
  // (one row per apartment, or per property for its unassigned rooms).
  // invoice_no is assigned by the assign_management_invoice_no trigger.
  //
  // fee_basis and description are NOT accepted from the caller: a manual
  // invoice must never inherit another month's computed basis (see
  // MANUAL_FEE_BASIS).
  //
  // owner_user_id is not accepted either, for the same reason and with a sharper
  // consequence. The caller only has the PROJECTED row's value, which is copied
  // from the anchor invoice — so an owner whose portal account was linked after
  // that invoice would have the stale id written onto this month and never see
  // it (the RLS policy matches auth.uid() = owner_user_id), with no later cron
  // run to repair it because the month is now occupied. Omitting the column lets
  // fill_management_invoice_owner_user resolve it from live data, through the
  // same resolver generate_management_invoices uses (20260826190000).
  /**
   * Create ONE property's month through the generator (COL-487), rather than
   * as a hand-typed invoice. For an OFFSET property only the generator writes a
   * live invoice: its fee follows the rent collected (COL-474) and closing it
   * early opens a remainder (COL-483). A hand-made one (createInvoice) does
   * neither and holds the month's slot so the generator skips it.
   *
   * `created` is false when the generator wrote nothing because the month was
   * already there (a race with the cron or another admin); `row` is then the
   * invoice that stands. `row` is null when it wrote nothing and none exists:
   * the property bills nothing this month.
   */
  const generatePropertyMonth = useMutation({
    mutationFn: async (input: {
      lessor_id: string;
      property_id: number;
      period_month: string;
      settlement: InvoiceSettlement;
    }): Promise<{ row: ManagementInvoiceRow | null; created: boolean }> => {
      const { data: createdCount, error } = await supabase.rpc(
        "generate_management_invoices",
        {
          p_period: input.period_month,
          p_property_id: input.property_id,
          p_lessor_id: input.lessor_id,
          // Only the document asked for: never also the building's transfer
          // invoice, which is created by hand at a confirmed amount.
          p_settlement: input.settlement,
        }
      );
      if (error) throw error;
      // The month's own invoice for this property and owner: remainder_seq 0,
      // and never a canceled one (20260925140000's unique key).
      const { data: row, error: readError } = await supabase
        .from("management_invoices")
        .select(
          `*, lessors:lessor_id ( id, name, email, address, city_postal )`
        )
        .eq("lessor_id", input.lessor_id)
        .eq("property_id", input.property_id)
        .is("apartment_id", null)
        .eq("settlement", input.settlement)
        .eq("period_month", input.period_month)
        .eq("remainder_seq", 0)
        .neq("status", "canceled")
        .maybeSingle();
      if (readError) throw readError;
      return {
        row: (row as ManagementInvoiceRow | null) ?? null,
        created: Number(createdCount) > 0,
      };
    },
    onSuccess: invalidate,
  });

  const createInvoice = useMutation({
    mutationFn: async (input: {
      lessor_id: string;
      amount_cents: number;
      period_month: string;
      due_date: string | null;
      /** The unit this invoice bills. Omit both for a portfolio-level row. */
      apartment_id?: number | null;
      property_id?: number | null;
      /** How the unit is named on the document — see manualUnitFeeBasis. */
      unit_title?: string | null;
      unit_name?: string | null;
      property_title?: string | null;
      /**
       * How the fee will be settled. Must match what the generator would have
       * written for this unit's collection mode, or creating the month by hand
       * turns an offset fee into one the owner is asked to transfer (or the
       * reverse). Omitted reads as "transfer", the DB default.
       */
      settlement?: InvoiceSettlement | null;
    }): Promise<ManagementInvoiceRow> => {
      const offset = isOffsetInvoice({ settlement: input.settlement });
      const { data, error } = await supabase
        .from("management_invoices")
        .insert({
          lessor_id: input.lessor_id,
          // Since 20260904120000 an invoice bills ONE unit, and the generator
          // sets both columns (property_id resolves the payable bank account,
          // apartment_id identifies the flat). A hand-created invoice has to
          // land on the same slot as the one the cron would have written, or
          // the two would double-bill the same apartment.
          property_id: input.property_id ?? null,
          apartment_id: input.apartment_id ?? null,
          period_month: input.period_month,
          amount_cents: input.amount_cents,
          currency: "eur",
          fee_basis: manualUnitFeeBasis(input),
          description: null,
          status: "pending",
          settlement: input.settlement ?? "transfer",
          // An offset invoice has no due date, by the same rule the generator
          // applies: a due date is what would let the read-time overdue
          // derivation dun an owner for a fee taken out of their rent.
          due_date: offset ? null : input.due_date,
        })
        .select(
          `*, lessors:lessor_id ( id, name, email, address, city_postal )`
        )
        .single();
      if (error) throw error;
      return data as ManagementInvoiceRow;
    },
    onSuccess: invalidate,
  });

  /**
   * Correct an ISSUED invoice the only way it can be corrected: cancel it and
   * issue a replacement carrying a new invoice_no (Storno + reissue).
   *
   * Delegates to the cancel_and_reissue_management_invoice RPC rather than
   * doing the two writes here, because they must be one transaction — a Storno
   * that lands without its replacement leaves the owner's month unbilled and
   * the admin with no obvious way to notice. The RPC re-checks the draft/paid/
   * already-canceled rules, so this is not the gate, just the caller.
   */
  const cancelAndReissue = useMutation({
    mutationFn: async ({
      id,
      reason,
      amount_cents,
      due_date,
    }: {
      id: string;
      reason: string;
      amount_cents: number;
      due_date: string | null;
    }): Promise<ManagementInvoiceRow> => {
      const { data, error } = await supabase.rpc(
        "cancel_and_reissue_management_invoice",
        {
          p_invoice_id: id,
          p_reason: reason,
          p_amount_cents: amount_cents,
          p_due_date: due_date,
        }
      );
      if (error) throw error;
      // RETURNS a single management_invoices row; PostgREST hands back the
      // object (or a one-element array depending on negotiation).
      const row = (Array.isArray(data) ? data[0] : data) as
        | ManagementInvoiceRow
        | null
        | undefined;
      if (!row) {
        throw new Error(
          "The invoice was not reissued — no replacement came back."
        );
      }
      return row;
    },
    onSuccess: invalidate,
  });

  // Amount/due-date edit — the owner-invoice equivalent of the tenant rent
  // row's inline edit. Status is untouched: paid/unpaid still goes through
  // markPaid/unmarkPaid so the proof+note gate can't be bypassed.
  const editInvoice = useMutation({
    mutationFn: async ({
      id,
      amount_cents,
      due_date,
    }: {
      id: string;
      amount_cents: number;
      due_date: string | null;
    }) => {
      // The same rule canEditManagementInvoice hides the button for, applied
      // where it actually binds: an issued (sent) or paid invoice is filtered
      // OUT of the update, so a stale row in a client that rendered before the
      // send — or any future surface that forgets the gate — cannot restate an
      // invoice the owner already holds. Matching zero rows is the refusal, so
      // ask for the ids back to tell "not allowed" from "written".
      const { data, error } = await supabase
        .from("management_invoices")
        .update({ amount_cents, due_date })
        .eq("id", id)
        .is("sent_at", null)
        .neq("status", "paid")
        .select("id");
      if (error) throw error;
      if (!data?.length) {
        throw new Error(
          "This invoice has already been issued to the owner (or is paid) and can no longer be changed. Cancel it and issue a corrected invoice instead."
        );
      }
    },
    onSuccess: invalidate,
  });

  /**
   * Settle an OFFSET invoice: record the note about the deduction and mark it
   * paid, in ONE write.
   *
   * One write on purpose. The DB paid gate reads metadata.proof_note off the
   * row being written, so a note-then-status pair would either need the status
   * write to re-send the note anyway, or fail the gate outright if the first
   * write raced. There is no proof file in this flow — for a settlement=offset
   * row the gate requires the note alone, because no bank transfer exists to
   * evidence (20260907110000 §3).
   */
  const confirmOffset = useMutation({
    mutationFn: async ({
      row,
      note,
      netPayoutCents = null,
    }: {
      row: ManagementInvoice;
      note: string;
      /**
       * The net payout this confirmation attests to, in cents — the figure the
       * dialog showed. Saved beside the note so the invoice history can say what
       * was paid out. Null where no live figure was available (the hub, an
       * older month): nothing is saved and the history reads "—".
       */
      netPayoutCents?: number | null;
    }) => {
      if (!isOffsetInvoice(row)) {
        throw new Error(
          "This invoice is paid by the owner, not deducted from rent — upload the payment proof instead."
        );
      }
      const trimmed = note.trim();
      if (!trimmed) {
        throw new Error("Add a note recording the deduction.");
      }
      const now = new Date().toISOString();
      // The row's old amount is dropped first: an invoice that was unmarked and
      // is being confirmed again must not keep the previous confirmation's
      // figure when this one has none to record.
      const {
        [OFFSET_NET_PAYOUT_METADATA_KEY]: _previousNetPayout,
        ...priorMetadata
      } = row.metadata ?? {};
      const recordsNetPayout =
        typeof netPayoutCents === "number" &&
        Number.isInteger(netPayoutCents) &&
        netPayoutCents >= 0;
      const { data, error } = await supabase
        .from("management_invoices")
        .update({
          status: "paid",
          paid_at: now,
          metadata: {
            ...priorMetadata,
            proof_note: trimmed,
            proof_note_at: now,
            // Distinguishes "settled by offset" from an ordinary paid invoice
            // whose note happens to mention rent, for the activity feed and any
            // later reconciliation.
            offset_confirmed_at: now,
            ...(recordsNetPayout
              ? { [OFFSET_NET_PAYOUT_METADATA_KEY]: netPayoutCents }
              : {}),
          },
        })
        .eq("id", row.id)
        // Same shape as editInvoice: the guard binds in the WHERE clause, so a
        // stale client cannot settle a row that has since been paid or voided.
        .eq("settlement", "offset")
        .neq("status", "paid")
        .neq("status", "canceled")
        .select("id");
      if (error) throw error;
      if (!data?.length) {
        throw new Error(
          "This invoice could not be settled — reload the page: it may already be settled or have been voided."
        );
      }
    },
    onSuccess: invalidate,
  });

  const deleteProof = useMutation({
    mutationFn: async ({
      id,
      proof_url,
      reason,
      metadata,
    }: {
      id: string;
      proof_url: string | null;
      reason: string;
      metadata: Record<string, unknown>;
    }) => {
      // Deletion requires a reason — keep an audit trail of every removal.
      const prior = Array.isArray(metadata?.proof_deletions)
        ? (metadata.proof_deletions as unknown[])
        : [];
      // Returned to the caller so the audit_logs row it writes can point at
      // this exact deletion; the owner Activity feed matches the two on it and
      // renders one entry instead of two.
      const deletedAt = new Date().toISOString();
      // Clear the DB reference FIRST. If this fails, the file is untouched and
      // proof_url still resolves — no dangling link. Only then remove the object.
      // Same ordering rule as uploadProof: clear the note before dropping the
      // proof columns. A merge that lands then a column update that fails
      // leaves the proof in place with no note — the paid gate closes, and the
      // deletion is still recorded in proof_deletions. The other order would
      // drop the proof and keep its note.
      const { error: mergeError } = await mergeInvoiceMetadata(id, {
        proof_note: null,
        proof_note_at: null,
        proof_deletions: [...prior, { reason, at: deletedAt }],
      });
      if (mergeError) throw mergeError;

      const { error } = await supabase
        .from("management_invoices")
        .update({
          proof_url: null,
          proof_uploaded_at: null,
        })
        .eq("id", id);
      if (error) throw error;
      // Best-effort cleanup: the row no longer points here, so an orphaned
      // object is just unused bytes — never fail the delete over it.
      if (proof_url) {
        const { error: removeError } = await supabase.storage
          .from(PROOF_BUCKET)
          .remove([proof_url]);
        if (removeError) {
          console.warn("Proof object cleanup failed (orphaned):", removeError);
        }
      }
      return { deletedAt };
    },
    onSuccess: invalidate,
  });

  return {
    uploadProof,
    markPaid,
    unmarkPaid,
    deleteProof,
    addProofNote,
    confirmOffset,
    createInvoice,
    generatePropertyMonth,
    editInvoice,
    cancelAndReissue,
  };
}
